import { FC, useState } from 'react';
import { Column } from 'react-table';
import { useTranslation } from 'react-i18next';
import { FaListAlt, FaFile, FaFileSignature, FaInfo, FaFilePrescription } from 'react-icons/fa';

import API from 'core/Api';
import { createErrorFromResponse } from 'core/Error';

import { useModal } from 'store/modal';
import { useActiveClientContext } from 'store/active-client';

import { formatDate } from 'utils/helpers/formatters';
import { isIntigerOrFloat } from 'utils/helpers/leaves';
import { buildClientEntity } from 'utils/helpers/client';

import Button from 'components/Button';
import TooltipWrapper from 'components/TooltipWrapper';
import { IАttachment } from 'components/Attachment';

import { getUserInfo } from 'utils/auth';
import { getSubdomain } from 'utils/helpers/routing';

import NotAuthorizedScreen from 'routes/not-authorized';
import MainLayout from 'components/main-layout/MainLayout';
import FetchTable from 'components/table/FetchTable';
import handlePreviewDoc from 'utils/helpers/handlePreviewDoc';
import Spinner from 'components/Spinner';
import { IMyLeave } from '../types';

const Screen: FC = () => {
  const { t } = useTranslation();
  const { client } = useActiveClientContext();
  const isOperator = getSubdomain() === 'operators';
  const isLeaveModuleActive = getUserInfo().featureLeavesIsActive;
  const { showModal } = useModal();
  const windowInnerWidth = window.innerWidth;
  const isMobile = windowInnerWidth < 768;
  const [isLoadingDownload, setIsLoadingDownload] = useState(false);
  const [isLoadingApprove, setIsLoadingApprove] = useState(false);
  const [isLoadingReject, setIsLoadingReject] = useState(false);

  const handleAttachmentClick = (id: number) => {
    return API.get(`leaves/attachment/${id}/download`)
      .then(res => {
        window.open(res.data, '_blank');
      })
      .catch(res => {
        const error = createErrorFromResponse(res);
        showModal({
          type: 'error',
          content: error.message,
        });
      });
  };
  const handleAttachmentsModal = (attachments: IАttachment[], commentText: string | undefined) => {
    const links = attachments.map(attachment => {
      return {
        id: attachment.id,
        fileName: `${attachment.file_name}.${attachment.extension}`,
      };
    });

    showModal({
      type: 'list',
      heading: t('leaves.attachmentsList'),
      onLinkClick: handleAttachmentClick,
      links,
      content: commentText ? (
        <span>
          <b style={{ fontSize: '20px' }}>Коментар от служител:</b> {commentText}
        </span>
      ) : undefined,
    });
  };

  const columns: Array<Column<IMyLeave>> = [
    {
      Header: 'ID',
      accessor: 'id',
    },
    {
      Header: t('leaves.type') || '',
      accessor: 'type_label',
      width: '20%',
      Cell: row => {
        return row.value || '';
      },
    },
    {
      Header: t('leaves.dateFrom') || '',
      accessor: 'date_from',
      width: '7.5rem',
      Cell: ({ cell }) => {
        const dateFrom = new Date(cell.row.original.date_from);
        const subtractedDaysByYears = cell.row.original.subtracted_days_by_years;

        // Filter out entries where days are 0
        const filteredEntries = subtractedDaysByYears
          ? Object.entries(subtractedDaysByYears).filter(([_, days]) => days !== 0)
          : [];

        const tooltipContent =
          filteredEntries.length > 0 ? (
            <div>
              {filteredEntries.map(([year, days]) => (
                <>
                  {t('leaves.fromYear')} {year} {t('leaves.yearShortOnlyBG')} {t('leaves.used')} {days}{' '}
                  {t('leaves.days')}
                  <br />
                </>
              ))}
            </div>
          ) : null;

        return tooltipContent ? (
          <TooltipWrapper
            id={`tooltip-date-from-${cell.row.id}`}
            overlay={tooltipContent}
          >
            <span>{formatDate(dateFrom, 'dd.mm.YYY') || ''}</span>
          </TooltipWrapper>
        ) : (
          <span>{formatDate(dateFrom, 'dd.mm.YYY') || ''}</span>
        );
      },
    },
    {
      Header: t('leaves.dateTo') || '',
      accessor: 'date_to',
      width: '7rem',
      Cell: ({ cell }) => {
        const dateTo = new Date(cell.row.original.date_to);
        const subtractedDaysByYears = cell.row.original.subtracted_days_by_years;

        // Filter out entries where days are 0
        const filteredEntries = subtractedDaysByYears
          ? Object.entries(subtractedDaysByYears).filter(([_, days]) => days !== 0)
          : [];

        const tooltipContent =
          filteredEntries.length > 0 ? (
            <div>
              {filteredEntries.map(([year, days]) => (
                <>
                  {t('leaves.fromYear')} {year} {t('leaves.yearShortOnlyBG')} {t('leaves.used')} {days}{' '}
                  {t('leaves.days')}
                  <br />
                </>
              ))}
            </div>
          ) : null;

        return tooltipContent ? (
          <TooltipWrapper
            id={`tooltip-date-to-${cell.row.id}`}
            overlay={tooltipContent}
          >
            <span>{formatDate(dateTo, 'dd.mm.YYY') || ''}</span>
          </TooltipWrapper>
        ) : (
          <span>{formatDate(dateTo, 'dd.mm.YYY') || ''}</span>
        );
      },
    },
    {
      Header: t('leaves.numberOfDays') || '',
      width: '5rem',
      accessor: 'leave_days',
      Cell: ({ cell }) => {
        const subtractedDaysByYears = cell.row.original.subtracted_days_by_years;
        const leaveDays = cell.row.original.leave_days;

        // Filter out entries where days are 0
        const filteredEntries = subtractedDaysByYears
          ? Object.entries(subtractedDaysByYears).filter(([_, days]) => days !== 0)
          : [];

        const tooltipContent =
          filteredEntries.length > 0 ? (
            <div>
              {filteredEntries.map(([year, days]) => (
                <>
                  {t('leaves.fromYear')} {year} {t('leaves.yearShortOnlyBG')} {t('leaves.used')} {days}{' '}
                  {t('leaves.days')}
                  <br />
                </>
              ))}
            </div>
          ) : null;

        return tooltipContent ? (
          <TooltipWrapper
            id={`tooltip-leave-days-${cell.row.id}`}
            overlay={tooltipContent}
          >
            <span>{isIntigerOrFloat(leaveDays) || ''}</span>
          </TooltipWrapper>
        ) : (
          <span>{isIntigerOrFloat(leaveDays) || ''}</span>
        );
      },
    },
    {
      Header: t('leaves.status') || '',
      accessor: 'status',
      Cell: ({ value }) => {
        const statuses: any = {
          initiated: t('leaves.initiated'),
          pending: t('leaves.pending'),
          signing: t('leaves.signing'),
          confirmed: t('leaves.confirmed'),
          approved: t('leaves.approved'),
          rejected: t('leaves.rejected'),
          cancelled: t('leaves.cancelled'),
        };
        return statuses[value] || '';
      },
    },
    {
      Header: () => <div className="text-right">{t('table.actions')}</div>,
      accessor: 'actions',
      width: '7.5rem',
      minWidth: 250,
      disableSortBy: true,
      Cell: cell => {
        const {
          request_document_id: requestDocument,
          order_document_id: orderDocument,
          reject_document_id: rejectDocument,
          status,
          attachments,
          comment: commentText,
        } = cell.row.original;
        return (
          <div className="text-right">
            {requestDocument && (
              <div className="d-inline-block ml-2 mb-1">
                <TooltipWrapper
                  id="tooltip-approve"
                  overlay={t('leaves.requestDocPreview')}
                >
                  <Button
                    type="button"
                    btnType="info"
                    size="sm"
                    disabled={isLoadingDownload}
                    onClick={() =>
                      handlePreviewDoc({ showModal, id: requestDocument, setIsLoading: setIsLoadingDownload })
                    }
                  >
                    {!isLoadingDownload && <FaFile size={14} />}

                    {isLoadingDownload && <Spinner size={14} />}
                  </Button>
                </TooltipWrapper>
              </div>
            )}
            {orderDocument && (
              <div className="d-inline-block ml-2 mb-1">
                <TooltipWrapper
                  id="tooltip-approve"
                  overlay={t('leaves.orderDocPreview')}
                >
                  <Button
                    type="button"
                    btnType="primary"
                    size="sm"
                    disabled={isLoadingApprove}
                    onClick={() =>
                      handlePreviewDoc({ showModal, id: orderDocument, setIsLoading: setIsLoadingApprove })
                    }
                  >
                    {!isLoadingApprove && <FaFileSignature size={14} />}

                    {isLoadingApprove && <Spinner size={14} />}
                  </Button>
                </TooltipWrapper>
              </div>
            )}
            {rejectDocument && status === 'rejected' && (
              <div className="d-inline-block ml-2">
                <TooltipWrapper
                  id="tooltip-approve"
                  overlay={t('leaves.orderDocPreview')}
                >
                  <Button
                    type="button"
                    btnType="danger"
                    size="sm"
                    disabled={isLoadingReject}
                    onClick={() =>
                      handlePreviewDoc({ showModal, id: rejectDocument, setIsLoading: setIsLoadingReject })
                    }
                  >
                    {!isLoadingReject && <FaFilePrescription size={14} />}

                    {isLoadingReject && <Spinner size={14} />}
                  </Button>
                </TooltipWrapper>
              </div>
            )}
            {attachments.length > 0 && (
              <div className="d-inline-block ml-2 mb-1">
                <TooltipWrapper
                  id="tooltip-attachments"
                  overlay={t('leaves.attachmentsList')}
                >
                  <Button
                    type="button"
                    btnType="info"
                    size="sm"
                    onClick={() => handleAttachmentsModal(attachments, commentText)}
                  >
                    <FaListAlt size={14} />
                  </Button>
                </TooltipWrapper>
              </div>
            )}
          </div>
        );
      },
    },
  ];

  const mobileColumns: Array<Column<IMyLeave>> = [
    {
      Header: 'ID',
      accessor: 'id',
    },
    {
      Header: t('leaves.type') || '',
      accessor: 'type_label',
      width: '35%',
      Cell: row => {
        return row.value || '';
      },
    },
    {
      Header: t('leaves.dateFromTo') || '',
      accessor: 'date_from_to',
      width: '35%',
      Cell: ({ cell }) => {
        return `${formatDate(new Date(cell.row.original.date_from), 'dd.mm.YYY')} - ${formatDate(
          new Date(cell.row.original.date_to),
          'dd.mm.YYY'
        )}`;
      },
    },
    {
      Header: t('leaves.numberOfDays') || '',
      accessor: 'leave_days',
      width: '30%',
      Cell: ({ cell }) => {
        return isIntigerOrFloat(cell?.value) || '';
      },
    },
  ];

  if (isLeaveModuleActive || isOperator) {
    return (
      <MainLayout title={t('leaves.previousTitle')}>
        <FetchTable<IMyLeave>
          entityName={buildClientEntity('my-leaves', client)}
          entityEndpoint="my-leaves/previous"
          columns={isMobile ? mobileColumns : columns}
          hideFilterBar
          isLeavesMobile
          pagination
        />
      </MainLayout>
    );
  }

  return <NotAuthorizedScreen />;
};

export default Screen;
